import { I18nProvider } from '@linka/i18n';
import { Toaster } from '@linka/ui/toaster';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertDialogProvider } from '@/ui/components/alert-dialog/alert-dialog-provider';
import { ADSProvider } from '@/ui/components/arandu/ads-provider';
import { CookiesPolicyBanner } from '@/ui/components/cookies-policy-banner';
import { toast } from '@/ui/components/toast';

const UiI18nBridge = ({ children }: { children: React.ReactNode }) => {
  // cast namespace: 'ui' is registered at runtime via registerWithI18next but not in the static resources type
  const { t: tRaw } = useTranslation('ui' as never);
  return <I18nProvider t={(key, vars) => String(tRaw(key, vars as never))}>{children}</I18nProvider>;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 1000 * 60,
    },
    mutations: {
      onError: ({ name, message }) => {
        toast({
          title: name,
          description: message,
          status: 'danger',
        });
      },
    },
  },
});

export const Providers = ({ children }: Pick<ComponentProps<typeof ADSProvider>, 'children'>) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools buttonPosition='bottom-left' initialIsOpen={false} />
      <Toaster
        position='top-right'
        expand
        visibleToasts={8}
        closeButton
        toastOptions={{
          unstyled: true,
          style: { width: '100%' },
        }}
      />
      <CookiesPolicyBanner />
      <AlertDialogProvider />
      <ADSProvider>
        <UiI18nBridge>{children}</UiI18nBridge>
      </ADSProvider>
    </QueryClientProvider>
  );
};
